import { configuration } from '../../../app/common-reducers.js';
import { updateFacetOptions } from '../../../features/facet-options/facet-options-actions.js';
import { registerCategoryFacet } from '../../../features/facets/category-facet-set/category-facet-set-actions.js';
import { categoryFacetSetReducer as categoryFacetSet } from '../../../features/facets/category-facet-set/category-facet-set-slice.js';
import { defaultCategoryFacetOptions } from '../../../features/facets/category-facet-set/category-facet-set-slice.js';
import { categoryFacetSearchSetReducer as categoryFacetSearchSet } from '../../../features/facets/facet-search-set/category/category-facet-search-set-slice.js';
import { executeFacetSearch, executeFieldSuggest, } from '../../../features/facets/facet-search-set/generic/generic-facet-search-actions.js';
import { facetSelect, logFacetSelect, } from '../../../features/facets/facet-set/facet-set-analytics-actions.js';
import { executeSearch } from '../../../features/search/search-actions.js';
import { searchReducer as search } from '../../../features/search/search-slice.js';
import { loadReducerError } from '../../../utils/errors.js';
import { buildController, } from '../../controller/headless-controller.js';
import { determineFacetId } from '../../core/facets/_common/facet-id-determinor.js';
import { buildCategoryFacetSearch } from '../../facets/category-facet/headless-category-facet-search.js';
/**
 * Creates a `CategoryFieldSuggestions` controller instance.
 *
 * This controller initializes a category facet under the hood, but exposes state and methods that are relevant for suggesting field values based on a query.
 * It's important not to initialize a category facet with the same `facetId` but different options, because only the options of the controller which is built first will be taken into account.
 *
 * @param engine The headless engine.
 * @param props The configurable `CategoryFieldSuggestions` controller properties.
 * @returns A `CategoryFieldSuggestions` controller instance.
 *
 * @group Controllers
 * @category CategoryFieldSuggestions
 */
export function buildCategoryFieldSuggestions(engine, props) {
    if (!loadCategoryFieldSuggestionsReducers(engine)) {
        throw loadReducerError;
    }
    const { facetSearch: facetSearchOptions, ...facetOptions } = props.options.facet;
    const facetId = determineFacetId(engine, facetOptions);
    engine.dispatch(registerCategoryFacet({
        ...defaultCategoryFacetOptions,
        ...facetOptions,
        facetId,
    }));
    const facetSearch = buildCategoryFacetSearch(engine, {
        options: { ...facetSearchOptions, facetId },
        executeFacetSearchActionCreator: executeFacetSearch,
        executeFieldSuggestActionCreator: executeFieldSuggest,
        select: (value) => {
            engine.dispatch(updateFacetOptions());
            engine.dispatch(executeSearch({
                legacy: logFacetSelect({ facetId, facetValue: value.rawValue }),
                next: facetSelect(),
            }));
        },
        isForFieldSuggestions: true,
    });
    const controller = buildController(engine);
    return {
        ...controller,
        ...facetSearch,
        updateText: function (text) {
            facetSearch.updateText(text);
            facetSearch.search();
        },
        get state() {
            return facetSearch.state;
        },
    };
}
function loadCategoryFieldSuggestionsReducers(engine) {
    engine.addReducers({
        categoryFacetSet,
        configuration,
        categoryFacetSearchSet,
        search,
    });
    return true;
}
